import clsx from "clsx";
import { useTranslations } from "use-intl";
import useNavAppSnapshot from "./hooks/useNavAppSnapshot";
import { useStoreState } from "./store/useStore";
import { popperTriggerHover, stylesText } from "./styles";
import { NavAppBridge, NavAppSnapshot } from "./types";

const useSpotActionsState = (): {
  appBridge: NavAppBridge;
  snapshot: NavAppSnapshot;
} | null => {
  const { appBridge, snapshot } = useNavAppSnapshot();

  if (snapshot?.app !== "spot" || snapshot.page !== "spot-trade" || !appBridge) {
    return null;
  }

  return { appBridge, snapshot };
};

export const SpotModeSwitch = () => {
  const { theme } = useStoreState();
  const state = useSpotActionsState();

  if (!state) {
    return null;
  }

  const { appBridge, snapshot } = state;
  const isCopyTradePage = !!snapshot.spot?.isCopyTradePage;
  const canShowCopyTrade = !!snapshot.spot?.canShowCopyTrade;
  const hasSwitchAction = !!appBridge.actions?.switchSpotTradeMode;
  const labels = snapshot.spot?.tradeModeLabels;
  const regularText = labels?.regular ?? "Regular";
  const copyText = labels?.copy ?? "Copy";

  if (!canShowCopyTrade || !hasSwitchAction) {
    return null;
  }

  return (
    <div
      className={clsx(
        "mr-[16px] flex h-[24px] items-center rounded-[4px] p-px max-[860px]:hidden",
        theme === "dark" ? "bg-dark-fill-secondary" : "bg-light-fill-secondary"
      )}
    >
      <button
        type="button"
        className={clsx(
          "flex h-[22px] cursor-pointer items-center rounded-[4px] border-0 bg-transparent px-[12px] py-0 text-[12px] font-medium leading-[18px]",
          !isCopyTradePage
            ? theme === "dark"
              ? "bg-dark-fill-tertiary text-dark-label"
              : "bg-light-fill-tertiary text-light-label"
            : theme === "dark"
              ? "text-dark-label-40 hover:text-dark-label"
              : "text-light-label-40 hover:text-light-label"
        )}
        onClick={() => appBridge.actions?.switchSpotTradeMode?.("standard")}
      >
        {regularText}
      </button>
      <button
        type="button"
        className={clsx(
          "flex h-[22px] cursor-pointer items-center rounded-[4px] border-0 bg-transparent px-[12px] py-0 text-[12px] font-medium leading-[18px]",
          isCopyTradePage
            ? theme === "dark"
              ? "bg-dark-fill-tertiary text-dark-label"
              : "bg-light-fill-tertiary text-light-label"
            : theme === "dark"
              ? "text-dark-label-40 hover:text-dark-label"
              : "text-light-label-40 hover:text-light-label"
        )}
        onClick={() => appBridge.actions?.switchSpotTradeMode?.("copyTrade")}
      >
        {copyText}
      </button>
    </div>
  );
};

export const SpotSettingsAction = () => {
  const { theme } = useStoreState();
  const state = useSpotActionsState();
  const t = useTranslations("header");

  if (!state?.appBridge.actions?.openSpotSettings) {
    return null;
  }

  return (
    <button
      type="button"
      aria-label={t("settings")}
      title={t("settings")}
      className={clsx(
        "ml-[24px] flex h-[48px] cursor-pointer items-center border-0 bg-transparent p-0",
        stylesText({ mode: theme }),
        popperTriggerHover({ mode: theme })
      )}
      onClick={() => state.appBridge.actions?.openSpotSettings?.()}
    >
      <span aria-hidden className="iconfont icon-setting-fill text-[20px] leading-none" />
    </button>
  );
};

export default SpotSettingsAction;
